import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, ArrowLeft, Crown, User, Mail, Calendar, LogOut, CreditCard, Menu, X, RefreshCw } from 'lucide-react';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Account = () => {
    const { user, logout, refreshUser } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [cancelingSubscription, setCancelingSubscription] = useState(false);
    const businessName = settings?.business_name || 'BGRemove';

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    const isProUser = user?.subscription_status === 'active';
    const creditsAvailable = user?.credits_available || 0;
    const monthlyCredits = user?.subscription_credits_monthly || 0;
    const subscriptionPlan = user?.subscription_plan || null;
    const subscriptionCancelAt = user?.subscription_cancel_at
        ? new Date(user.subscription_cancel_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : null;
    const subscriptionPeriodEnd = user?.subscription_period_end
        ? new Date(user.subscription_period_end).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : null;

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to monthly credits at the end of your billing period.')) {
            return;
        }

        setCancelingSubscription(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/subscription/cancel`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            toast.success('Subscription canceled successfully');
            await refreshUser();
        } catch (error) {
            console.error('Cancel subscription error:', error);
            toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
        } finally {
            setCancelingSubscription(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="account-logo-link">
                            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                <Upload className="w-4 h-4 text-primary-foreground" />
                            </div>
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center gap-4">
                            <Button onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="account-dashboard-btn">
                                Dashboard
                            </Button>
                        </div>

                        {/* Mobile menu button */}
                        <button 
                            className="md:hidden p-2" 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="account-mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-border bg-background p-4">
                        <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full" data-testid="account-mobile-dashboard-btn">
                            Dashboard
                        </Button>
                    </div>
                )}
            </nav>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <Link 
                        to="/dashboard" 
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                        data-testid="account-back-link"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to dashboard
                    </Link>

                    <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mb-8">
                        Account Settings
                    </h1>

                    {/* Profile Section */}
                    <div className="glass-card rounded-2xl p-6 mb-6" data-testid="profile-section">
                        <h2 className="font-heading font-semibold text-lg mb-4">Profile</h2>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                                    <User className="w-6 h-6 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="font-medium" data-testid="profile-name">{user?.name}</p>
                                    <p className="text-sm text-muted-foreground" data-testid="profile-email">{user?.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                <span data-testid="profile-email-display">{user?.email}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span data-testid="profile-joined">
                                    Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    }) : 'Recently'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Section */}
                    <div className="glass-card rounded-2xl p-6 mb-6" data-testid="subscription-section">
                        <h2 className="font-heading font-semibold text-lg mb-4">Subscription & Credits</h2>

                        {/* Current Plan */}
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-2">
                                {isProUser ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 text-primary" />
                                        <span className="font-medium" data-testid="subscription-status">
                                            {monthlyCredits} Credits/Month Subscription
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                                        <span className="font-medium" data-testid="subscription-status">Free Plan</span>
                                    </>
                                )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2" data-testid="subscription-detail">
                                {isProUser
                                    ? `${creditsAvailable} credits available (rollover enabled)`
                                    : `${creditsAvailable} free credits available`
                                }
                            </p>
                            {subscriptionCancelAt && (
                                <p className="text-sm text-amber-600 dark:text-amber-500 mb-2" data-testid="subscription-cancel-notice">
                                    ⚠️ Subscription will end on {subscriptionCancelAt}
                                </p>
                            )}
                            {isProUser && subscriptionPeriodEnd && !subscriptionCancelAt && (
                                <p className="text-xs text-muted-foreground mb-2" data-testid="subscription-renewal">
                                    Next renewal: {subscriptionPeriodEnd}
                                </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-3">
                                {isProUser ? (
                                    <>
                                        <Button
                                            onClick={() => navigate('/pricing')}
                                            variant="outline"
                                            className="rounded-full"
                                            data-testid="change-plan-btn"
                                        >
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Change Plan
                                        </Button>
                                        <Button
                                            onClick={handleCancelSubscription}
                                            variant="outline"
                                            className="rounded-full text-destructive hover:text-destructive"
                                            disabled={cancelingSubscription}
                                            data-testid="cancel-subscription-btn"
                                        >
                                            {cancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={() => navigate('/pricing')}
                                        className="rounded-full"
                                        data-testid="upgrade-btn"
                                    >
                                        <Crown className="w-4 h-4 mr-2" />
                                        Subscribe
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Usage Stats */}
                    <div className="glass-card rounded-2xl p-6 mb-6" data-testid="usage-section">
                        <h2 className="font-heading font-semibold text-lg mb-4">Credit Balance</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-secondary/50 rounded-xl p-4">
                                <p className="text-2xl font-heading font-bold text-primary" data-testid="credits-available">
                                    {creditsAvailable}
                                </p>
                                <p className="text-sm text-muted-foreground">Available Credits</p>
                            </div>
                            {isProUser && (
                                <div className="bg-secondary/50 rounded-xl p-4">
                                    <p className="text-2xl font-heading font-bold text-primary" data-testid="monthly-credits">
                                        {monthlyCredits}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Credits/Month</p>
                                </div>
                            )}
                        </div>
                        {isProUser && (
                            <p className="text-xs text-muted-foreground mt-4">
                                Unused credits roll over each month while your subscription is active
                            </p>
                        )}
                    </div>

                    {/* Logout Section */}
                    <div className="glass-card rounded-2xl p-6" data-testid="logout-section">
                        <h2 className="font-heading font-semibold text-lg mb-4">Session</h2>
                        <Button 
                            variant="destructive"
                            onClick={handleLogout}
                            className="rounded-full"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign out
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Account;
